import React from 'react'
import { useController } from 'react-hook-form'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { HTMLRenderer, body2BaseStyle } from '@ui-kit/components/HTMLRenderer'
import { Checkbox } from '@ui-kit/components/formComponents/Checkbox'
import { body2 } from '@ui-kit/core/typography'
import { theme } from '@ui-kit/theme'

interface Props {
  label: string
  name: string
  testID?: string
}

const checkboxField = tv({
  slots: {
    container: 'flex-shrink',
    contentContainer: 'flex flex-row items-start gap-x-2',
    labelPressable: 'flex-shrink',
    errorMessage: [body2(), 'pt-1 text-error'],
  },
})

export const CheckboxField = (props: Props) => {
  const [width, setWidth] = React.useState(0)
  const { field, fieldState } = useController({
    name: props.name,
  })
  const source = React.useMemo(() => {
    return {
      html: props.label,
    }
  }, [props.label])
  const error = fieldState.error?.message

  const handleChange = (value: boolean) => {
    field.onChange(value)
  }

  const onLabelPress = () => {
    handleChange(!field.value)
  }

  const hasError = error !== undefined

  // With the HTMLRenderer we can only use normal styles,
  // it does not support any other type of styling
  const htmlStyles = React.useMemo(
    () => ({ ...body2BaseStyle, color: hasError ? theme.colors.error : theme.colors.black }),
    [hasError]
  )

  const { container, contentContainer, labelPressable, errorMessage } = checkboxField()

  return (
    <View className={container()} onLayout={(event) => setWidth(event.nativeEvent.layout.width)}>
      <View className={contentContainer()}>
        <Checkbox
          hasError={hasError}
          testID={props.testID}
          onPress={handleChange}
          isChecked={field.value}
        />
        <Pressable className={labelPressable()} onPress={onLabelPress}>
          <HTMLRenderer source={source} baseStyle={htmlStyles} contentWidth={width} />
        </Pressable>
      </View>

      {hasError && typeof error === 'string' && error.length > 0 && (
        <Text className={errorMessage()}>{error}</Text>
      )}
    </View>
  )
}
